import Button from '@rio-cloud/rio-uikit/Button';
import Notification from '@rio-cloud/rio-uikit/components/notification/Notification';
import ConfirmationDialog from '@rio-cloud/rio-uikit/ConfirmationDialog';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import SortDirection from '@rio-cloud/rio-uikit/utils/SortUtils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { getUserAccount, getUserProfile } from '@/configuration/login/loginSlice';
import { useAppDispatch, useAppSelector } from '@/configuration/setup/hooks';
import { useSendQuestionMutation } from '@/services/chatApi/chatApi';
import { EResponseStatus } from '@/services/config/types';
import { useDeleteThreadsMutation, useLazyFetchThreadsQuery } from '@/services/threadApi/threadApi';
import { mapFetchThreadsRequestParams } from '@/services/threadApi/utils/mapFetchThreadsRequestParams';
import { setScrollPosition } from '@/store/sidebar/sidebarSlice';
import { resetActiveThread, selectPagination } from '@/store/thread/threadSlice';
import { getUserId } from '@/utils/getUserId';
import { forceModalClose } from '@/utils/modalSub';
import { isSortDirection } from '@/utils/sortHelper/sortHelper';

import { useThreadPagination } from '../hooks/useThreadPagination';
import { useThreadSearch } from '../hooks/useThreadSearch';
import { useThreadSorting } from '../hooks/useThreadSorting';
import ThreadsList from '../ThreadsList/ThreadsList';
import { TUnifiedThreadsContainerProps } from './types';

const texts = {
  confirmDeletionTitle: 'chatPage.chatThreadList.confirmAllDeletion.title',
  confirmDeletionContent: 'chatPage.chatThreadList.confirmAllDeletion.content',
  threadsDeletionSuccess: 'chatPage.chatThreadList.threadsDeletion.success.notification',
  threadsDeletionError: 'chatPage.chatThreadList.threadsDeletion.error',
  cancel: 'common.cancel',
  confirm: 'common.confirm',
  clearAll: 'chatPage.chatThreadList.clearAll.label',
} as const;

const allThreadsDeleteConfirmationMessage = (
  <FormattedMessage id={texts.confirmDeletionContent} values={{ b: (chunks) => <strong>{chunks}</strong> }} />
);

// INFO: Unified container component for thread lists, it manages all shared logic for both AllThreads and FavoritesThreads lists

const ThreadsListContainer = ({ config }: TUnifiedThreadsContainerProps) => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const user = useAppSelector(getUserProfile);
  const userId = getUserId(user?.sub ?? '');
  const account = useAppSelector(getUserAccount);
  const [fetchThreads, { isLoading: isFetchThreadsLoading }] = useLazyFetchThreadsQuery();
  const [showDeleteActionDialog, setShowDeleteActionDialog] = useState(false);
  const [deleteThreads, { isLoading: isLoadingDeleteThreads }] = useDeleteThreadsMutation();
  const deleteAction = !config.favoriteOnly;

  const pagination = useAppSelector(selectPagination);

  const sortOrder = useMemo(
    () => (isSortDirection(pagination?.sortOrder) ? pagination?.sortOrder : SortDirection.DESCENDING),
    [pagination?.sortOrder],
  );

  const { searchValue, handleSearch } = useThreadSearch(userId, sortOrder, config.favoriteOnly);

  const { threadsList } = useThreadSorting({ favoriteOnly: config.favoriteOnly });

  const { loadMoreThreads } = useThreadPagination({
    userId,
    searchValue,
    sortOrder,
    favoriteOnly: config.favoriteOnly,
    hasNextPage: pagination?.hasNextPage,
    currentPage: pagination?.currentPage,
  });

  const toggleSortOrder = () => {
    const newSortOrder = sortOrder === SortDirection.DESCENDING ? SortDirection.ASCENDING : SortDirection.DESCENDING;
    fetchThreads(
      mapFetchThreadsRequestParams({
        userId,
        sortOrder: newSortOrder,
        search: searchValue,
        onlyFavorites: config.favoriteOnly,
      }),
      true,
    );
    dispatch(setScrollPosition(0));
  };

  const handleConfirmationDialog = () => {
    forceModalClose();
    setShowDeleteActionDialog(false);
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await deleteThreads({ userId }).unwrap();
      if (response.status === EResponseStatus.SUCCESS) {
        Notification.success(intl.formatMessage({ id: texts.threadsDeletionSuccess }));
        dispatch(resetActiveThread());
      } else {
        Notification.error(intl.formatMessage({ id: texts.threadsDeletionError }));
      }
    } catch {
      Notification.error(intl.formatMessage({ id: texts.threadsDeletionError }));
    } finally {
      handleConfirmationDialog();
    }
  };

  const deleteButtonClick = useCallback(() => {
    forceModalClose();
    setShowDeleteActionDialog(true);
    dispatch(setScrollPosition(0));
  }, []);

  // INFO: Initial fetch on mount, repeated when the Fleet changes because threads are fleet specific
  useEffect(() => {
    fetchThreads(
      mapFetchThreadsRequestParams({
        userId,
        search: searchValue,
        onlyFavorites: config.favoriteOnly,
      }),
    );
  }, [fetchThreads, userId, account, config.favoriteOnly]);

  const [, { isLoading: isSendMessageLoading }] = useSendQuestionMutation();

  const isDeleteAllDisabled = isLoadingDeleteThreads || isSendMessageLoading;

  const clearAll = useMemo(() => {
    return deleteAction ? (
      <Button
        data-testid="delete-all-threads-button"
        bsStyle="default"
        className="margin-top-2"
        iconName="rioglyph rioglyph-trash"
        onClick={deleteButtonClick}
        variant="link"
        disabled={isDeleteAllDisabled}
      >
        <FormattedMessage id={texts.clearAll} />
      </Button>
    ) : undefined;
  }, [intl, deleteButtonClick, isDeleteAllDisabled]);

  const confirmAllThreadsDeletionButton = useMemo(() => {
    return (
      <>
        <span className="rioglyph rioglyph-ok margin-right-5" />
        <span>{intl.formatMessage({ id: texts.confirm })}</span>
      </>
    );
  }, [intl]);

  if (isFetchThreadsLoading) return <Spinner isDoubleSized />;

  return (
    <>
      <ThreadsList
        texts={{
          title: intl.formatMessage({
            id: config.favoriteOnly ? 'chatPage.chatThreadList.favorite.title' : 'chatPage.chatThreadList.title',
          }),
          noMessage: intl.formatMessage({
            id: config.favoriteOnly
              ? 'chatPage.chatThreadList.threadAddToFavorites.noFavorites.label'
              : 'chatPage.chatThreadList.error.noThreads',
          }),
        }}
        threads={threadsList}
        searchQuery={searchValue}
        onSearch={handleSearch}
        onSortChange={toggleSortOrder}
        onLoadMore={loadMoreThreads}
        isLoading={isLoadingDeleteThreads}
        isSearchDisabled={isSendMessageLoading}
        headerAction={clearAll}
      />

      {deleteAction && showDeleteActionDialog && (
        <ConfirmationDialog
          show={showDeleteActionDialog}
          title={intl.formatMessage({ id: texts.confirmDeletionTitle })}
          content={allThreadsDeleteConfirmationMessage}
          onClickConfirm={handleConfirmDelete}
          onClickCancel={handleConfirmationDialog}
          cancelButtonText={intl.formatMessage({ id: texts.cancel })}
          confirmButtonText={confirmAllThreadsDeletionButton}
        />
      )}
    </>
  );
};

export default ThreadsListContainer;
